if (!customElements.get('featured-collection-filter')) {
  class FeaturedCollectionFilter extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.sectionId = this.dataset.sectionId || 'featured-collection';
      this.priceLimit = Number(this.dataset.priceMax) || 99999;
      this.currencyCode = this.dataset.currencyCode || 'USD';
      this.currencySymbol = this.dataset.currencySymbol || '$';
      this.removeIconUrl = this.dataset.removeIcon || '';
      this.previousIconUrl = this.dataset.previousIcon || '';
      this.nextIconUrl = this.dataset.nextIcon || '';
      this.pageSize = Number(this.dataset.pageSize) || 12;
      this.currentPage = 1;
      this.selectedCategories = new Set();
      this.selectedAvailability = new Set();
      this.selectedColors = new Set();
      this.priceMin = 0;
      this.priceMax = this.priceLimit;
      this.sortValue = 'best-selling';
      this.lastFocusedElement = null;
      this.currentMatches = [];

      this.grid = this.querySelector('[data-filter-grid]');
      this.activeFilters = this.querySelector('[data-active-filters]');
      this.emptyState = this.querySelector('[data-filter-empty]');
      this.drawer = this.querySelector('[data-filter-drawer]');
      this.drawerStackingContext = this.closest('.isolate');
      this.productEntries = this.readProducts();
      this.categories = this.readCategories();
      this.colors = this.readColors();

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleExternalCategorySelect = this.handleExternalCategorySelect.bind(this);

      this.buildFacetOptions();
      this.addEventListener('click', this.handleClick);
      this.addEventListener('change', this.handleChange);
      this.addEventListener('input', this.handleInput);
      this.addEventListener('focusin', this.handleFocusIn);
      document.addEventListener('keydown', this.handleKeydown);
      document.addEventListener('home-category:select', this.handleExternalCategorySelect);
      window.addEventListener('resize', this.handleResize);
      this.syncPriceControls();
      this.applyFilters();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('home-category:select', this.handleExternalCategorySelect);
      window.removeEventListener('resize', this.handleResize);
      document.body.classList.remove('featured-filter-drawer-open');
      this.drawerStackingContext?.classList.remove('featured-filter-drawer-layer');
    }

    readProducts() {
      return [...this.querySelectorAll('[data-filter-product]')]
        .map((item) => {
          const dataElement = item.querySelector('[data-filter-product-data]');
          if (!dataElement) return null;

          try {
            const data = JSON.parse(dataElement.textContent);
            return {
              element: item,
              index: Number(data.index) || 0,
              title: String(data.title || ''),
              price: Number(data.price) / 100,
              available: Boolean(data.available),
              categories: new Set((data.categories || []).map((value) => String(value))),
              colors: (data.colors || []).map((color) => ({
                name: String(color.name || '').trim(),
                key: this.normalize(color.name),
                swatch: color.swatch || '',
                image: color.image || '',
              })),
            };
          } catch (error) {
            console.error('Unable to parse featured collection product filter data.', error);
            return null;
          }
        })
        .filter(Boolean);
    }

    readCategories() {
      const dataElement =
        this.querySelector('[data-filter-category-data]') || document.querySelector('[data-home-category-data]');
      if (!dataElement) return [];

      try {
        return JSON.parse(dataElement.textContent)
          .map((category) => ({
            handle: String(category.handle || ''),
            title: String(category.title || category.handle || ''),
          }))
          .filter((category) => category.handle);
      } catch (error) {
        console.error('Unable to parse home category filter data.', error);
        return [];
      }
    }

    readColors() {
      const colors = new Map();

      this.productEntries.forEach((product) => {
        product.colors.forEach((color) => {
          if (!color.key || colors.has(color.key)) return;
          colors.set(color.key, color);
        });
      });

      return [...colors.values()].sort((first, second) => first.name.localeCompare(second.name));
    }

    normalize(value) {
      return String(value || '').trim().toLocaleLowerCase();
    }

    buildFacetOptions() {
      this.querySelectorAll('[data-category-options]').forEach((list, listIndex) => {
        list.replaceChildren(
          ...this.categories.map((category, optionIndex) =>
            this.createOption({
              kind: 'category',
              value: category.handle,
              label: category.title,
              id: `${this.sectionId}-category-${listIndex}-${optionIndex}`,
            })
          )
        );
      });

      this.querySelectorAll('[data-color-options]').forEach((list, listIndex) => {
        list.replaceChildren(
          ...this.colors.map((color, optionIndex) =>
            this.createOption({
              kind: 'color',
              value: color.key,
              label: color.name,
              id: `${this.sectionId}-color-${listIndex}-${optionIndex}`,
              color,
            })
          )
        );
      });

      const availabilityOptions = [
        { value: 'in-stock', label: 'In stock' },
        { value: 'out-of-stock', label: 'Out of stock' },
      ];
      this.querySelectorAll('[data-availability-options]').forEach((list, listIndex) => {
        list.replaceChildren(
          ...availabilityOptions.map((option, optionIndex) =>
            this.createOption({
              kind: 'availability',
              value: option.value,
              label: option.label,
              id: `${this.sectionId}-availability-${listIndex}-${optionIndex}`,
            })
          )
        );
      });
    }

    createOption({ kind, value, label, id, color }) {
      const item = document.createElement('li');
      item.className = 'featured-collection-filter__option';
      item.dataset.optionKind = kind;
      item.dataset.optionValue = value;

      const optionLabel = document.createElement('label');
      optionLabel.className = 'featured-collection-filter__option-label';
      optionLabel.htmlFor = id;

      const input = document.createElement('input');
      input.className = 'featured-collection-filter__checkbox';
      input.type = 'checkbox';
      input.id = id;
      input.value = value;
      input.dataset.filterKind = kind;

      const checkboxMark = document.createElement('span');
      checkboxMark.className = 'featured-collection-filter__checkbox-mark';
      checkboxMark.setAttribute('aria-hidden', 'true');

      optionLabel.append(input, checkboxMark);

      if (color) {
        const swatch = document.createElement('span');
        swatch.className = 'featured-collection-filter__swatch';
        swatch.setAttribute('aria-hidden', 'true');
        this.setSwatchStyle(swatch, color);
        optionLabel.append(swatch);
      }

      const text = document.createElement('span');
      text.className = 'featured-collection-filter__option-text';
      text.append(document.createTextNode(label), ' (');

      const count = document.createElement('span');
      count.dataset.optionCount = '';
      count.textContent = '0';
      text.append(count, ')');

      optionLabel.append(text);
      item.append(optionLabel);
      return item;
    }

    setSwatchStyle(swatch, color) {
      if (color.image) {
        swatch.style.backgroundImage = `url(${JSON.stringify(color.image)})`;
        return;
      }

      if (color.swatch) {
        swatch.style.background = color.swatch;
        return;
      }

      swatch.style.background = this.getFallbackColor(color.name);
    }

    getFallbackColor(name) {
      const normalized = this.normalize(name);
      const colorMap = {
        beige: '#e5d3b3',
        black: '#111111',
        blue: '#2864b7',
        brown: '#795548',
        gold: '#d99a00',
        gray: '#8a8a8a',
        grey: '#8a8a8a',
        green: '#00ad48',
        navy: '#26249b',
        orange: '#ff7900',
        pink: '#f6a8ba',
        purple: '#7447a8',
        red: '#d72828',
        silver: '#b8bcc2',
        white: '#ffffff',
        yellow: '#f2cf2f',
      };

      if (normalized.includes('multi')) {
        return 'conic-gradient(#d72828, #f2cf2f, #00ad48, #2864b7, #d72828)';
      }

      for (const [colorName, colorValue] of Object.entries(colorMap)) {
        if (normalized.includes(colorName)) return colorValue;
      }

      let hash = 0;
      for (let index = 0; index < normalized.length; index += 1) {
        hash = normalized.charCodeAt(index) + ((hash << 5) - hash);
      }
      return `hsl(${Math.abs(hash) % 360} 32% 58%)`;
    }

    handleClick(event) {
      const openButton = event.target.closest('[data-filter-open]');
      if (openButton) {
        this.openDrawer(openButton);
        return;
      }

      if (event.target.closest('[data-filter-close], [data-filter-backdrop]')) {
        this.closeDrawer();
        return;
      }

      const toggle = event.target.closest('[data-facet-toggle]');
      if (toggle) {
        const content = document.getElementById(toggle.getAttribute('aria-controls'));
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isExpanded));
        if (content) content.hidden = isExpanded;
        return;
      }

      const chip = event.target.closest('[data-remove-filter]');
      if (chip) {
        this.removeFilter(chip.dataset.removeFilter, chip.dataset.filterValue);
        return;
      }

      if (event.target.closest('[data-clear-filters]')) {
        this.clearFilters();
        return;
      }

      const pageButton = event.target.closest('[data-filter-page]');
      if (pageButton) {
        this.currentPage = Number(pageButton.dataset.filterPage) || 1;
        this.renderPagination(this.currentMatches.length);
        this.renderPage(this.currentMatches);
        this.focusPageButton(this.currentPage);
      }
    }

    handleChange(event) {
      const checkbox = event.target.closest('[data-filter-kind]');
      if (checkbox) {
        const kind = checkbox.dataset.filterKind;
        let selectedSet = this.selectedColors;
        if (kind === 'category') selectedSet = this.selectedCategories;
        if (kind === 'availability') selectedSet = this.selectedAvailability;
        if (checkbox.checked) {
          selectedSet.add(checkbox.value);
        } else {
          selectedSet.delete(checkbox.value);
        }
        this.syncCheckboxes(kind, checkbox.value, checkbox.checked);
        this.applyFilters();
        return;
      }

      const priceInput = event.target.closest('[data-price-input]');
      if (priceInput) {
        this.updatePrice(priceInput);
        return;
      }

      const sort = event.target.closest('[data-filter-sort]');
      if (sort) {
        this.sortValue = sort.value;
        this.querySelectorAll('[data-filter-sort]').forEach((select) => {
          select.value = this.sortValue;
        });
        this.applyFilters();
      }
    }

    handleInput(event) {
      const priceInput = event.target.closest('[data-price-input]');
      if (!priceInput || priceInput.value === '') return;
      this.updatePrice(priceInput);
    }

    handleFocusIn(event) {
      const priceInput = event.target.closest('[data-price-text-input]');
      if (!priceInput) return;
      priceInput.value = '';
    }

    updatePrice(input) {
      const role = input.dataset.priceRole;
      const value = this.clampPrice(input.value);

      if (role === 'min') {
        this.priceMin = value;
      } else {
        this.priceMax = value;
      }

      this.syncPriceControls();
      this.applyFilters();
    }

    clampPrice(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) return 0;
      return Math.max(0, Math.min(number, this.priceLimit));
    }

    syncPriceControls() {
      this.querySelectorAll('[data-price-role="min"]').forEach((input) => {
        input.value = this.priceMin;
      });
      this.querySelectorAll('[data-price-role="max"]').forEach((input) => {
        input.value = this.priceMax;
      });

      const start = (Math.min(this.priceMin, this.priceMax) / this.priceLimit) * 100;
      const end = (Math.max(this.priceMin, this.priceMax) / this.priceLimit) * 100;
      this.querySelectorAll('[data-price-range-wrap]').forEach((range) => {
        range.style.setProperty('--range-start', `${start}%`);
        range.style.setProperty('--range-end', `${end}%`);
      });
    }

    syncCheckboxes(kind, value, checked) {
      this.querySelectorAll(`[data-filter-kind="${kind}"]`).forEach((checkbox) => {
        if (checkbox.value === value) checkbox.checked = checked;
      });
    }

    selectCategory(value) {
      const category = String(value || '');
      if (!category || !this.categories.some((item) => item.handle === category)) return;

      this.selectedCategories.clear();
      this.selectedAvailability.clear();
      this.selectedColors.clear();
      this.priceMin = 0;
      this.priceMax = this.priceLimit;
      this.querySelectorAll('[data-filter-kind]').forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.syncPriceControls();

      this.selectedCategories.add(category);
      this.syncCheckboxes('category', category, true);
      this.applyFilters();
    }

    handleExternalCategorySelect(event) {
      this.selectCategory(event.detail?.handle);
    }

    applyFilters() {
      this.currentPage = 1;
      this.currentMatches = this.productEntries.filter((product) => this.matchesAllFilters(product));

      this.updateFacetCounts();
      this.renderActiveFilters();
      this.updateResultCount(this.currentMatches.length);
      this.emptyState.hidden = this.currentMatches.length !== 0;
      this.renderPagination(this.currentMatches.length);
      this.renderPage(this.currentMatches);
    }

    matchesAllFilters(product) {
      return (
        this.matchesPrice(product) &&
        this.matchesCategories(product) &&
        this.matchesAvailability(product) &&
        this.matchesColors(product)
      );
    }

    matchesPrice(product) {
      const lowerBound = Math.min(this.priceMin, this.priceMax);
      const upperBound = Math.max(this.priceMin, this.priceMax);
      return product.price >= lowerBound && product.price <= upperBound;
    }

    matchesCategories(product) {
      if (!this.selectedCategories.size) return true;
      return [...this.selectedCategories].some((category) => product.categories.has(category));
    }

    matchesAvailability(product) {
      if (!this.selectedAvailability.size || this.selectedAvailability.size === 2) return true;
      return this.selectedAvailability.has(product.available ? 'in-stock' : 'out-of-stock');
    }

    matchesColors(product) {
      if (!this.selectedColors.size) return true;
      return product.colors.some((color) => this.selectedColors.has(color.key));
    }

    updateFacetCounts() {
      const categoryCounts = new Map();
      this.categories.forEach((category) => {
        const count = this.productEntries.filter(
          (product) =>
            this.matchesPrice(product) &&
            this.matchesAvailability(product) &&
            this.matchesColors(product) &&
            product.categories.has(category.handle)
        ).length;
        categoryCounts.set(category.handle, count);
      });

      const availabilityCounts = new Map();
      ['in-stock', 'out-of-stock'].forEach((availability) => {
        const count = this.productEntries.filter(
          (product) =>
            this.matchesPrice(product) &&
            this.matchesCategories(product) &&
            this.matchesColors(product) &&
            (availability === 'in-stock' ? product.available : !product.available)
        ).length;
        availabilityCounts.set(availability, count);
      });

      const colorCounts = new Map();
      this.colors.forEach((color) => {
        const count = this.productEntries.filter(
          (product) =>
            this.matchesPrice(product) &&
            this.matchesCategories(product) &&
            this.matchesAvailability(product) &&
            product.colors.some((productColor) => productColor.key === color.key)
        ).length;
        colorCounts.set(color.key, count);
      });

      this.updateOptionCounts('category', categoryCounts, this.selectedCategories);
      this.updateOptionCounts('availability', availabilityCounts, this.selectedAvailability);
      this.updateOptionCounts('color', colorCounts, this.selectedColors);
    }

    updateOptionCounts(kind, counts, selectedValues) {
      this.querySelectorAll(`[data-option-kind="${kind}"]`).forEach((option) => {
        const value = option.dataset.optionValue;
        const count = counts.get(value) || 0;
        const countElement = option.querySelector('[data-option-count]');
        if (countElement) countElement.textContent = count;
        option.hidden = kind !== 'availability' && count === 0 && !selectedValues.has(value);
      });
    }

    renderActiveFilters() {
      const filters = [];
      const categoryNames = new Map(this.categories.map((category) => [category.handle, category.title]));
      const availabilityNames = new Map([
        ['in-stock', 'In stock'],
        ['out-of-stock', 'Out of stock'],
      ]);
      const colorNames = new Map(this.colors.map((color) => [color.key, color.name]));

      this.selectedCategories.forEach((value) => {
        filters.push({ kind: 'category', value, label: categoryNames.get(value) || value });
      });
      this.selectedAvailability.forEach((value) => {
        filters.push({ kind: 'availability', value, label: availabilityNames.get(value) || value });
      });
      this.selectedColors.forEach((value) => {
        filters.push({ kind: 'color', value, label: colorNames.get(value) || value });
      });
      if (this.priceMin > 0 || this.priceMax < this.priceLimit) {
        const lowerBound = Math.min(this.priceMin, this.priceMax);
        const upperBound = Math.max(this.priceMin, this.priceMax);
        filters.push({
          kind: 'price',
          value: '',
          label: `${this.formatPrice(lowerBound)} - ${this.formatPrice(upperBound)}`,
        });
      }

      this.activeFilters.replaceChildren();
      this.activeFilters.hidden = filters.length === 0;

      filters.forEach((filter) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'featured-collection-filter__chip';
        button.dataset.removeFilter = filter.kind;
        button.dataset.filterValue = filter.value;
        button.setAttribute('aria-label', `Remove ${filter.label} filter`);
        if (this.removeIconUrl) {
          const icon = document.createElement('img');
          icon.className = 'featured-collection-filter__chip-icon';
          icon.src = this.removeIconUrl;
          icon.alt = '';
          icon.setAttribute('aria-hidden', 'true');
          button.append(icon);
        }
        const label = document.createElement('span');
        label.textContent = filter.label;
        button.append(label);
        this.activeFilters.append(button);
      });

      if (filters.length) {
        const clearButton = document.createElement('button');
        clearButton.type = 'button';
        clearButton.className = 'featured-collection-filter__clear';
        clearButton.dataset.clearFilters = '';
        clearButton.textContent = 'Clear All';
        this.activeFilters.append(clearButton);
      }
    }

    formatPrice(value) {
      const number = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
      return `${this.currencySymbol}${number}`;
    }

    updateResultCount(filteredCount) {
      let message = `Showing ${filteredCount} results`;
      if (filteredCount > this.pageSize) {
        const start = (this.currentPage - 1) * this.pageSize + 1;
        const end = Math.min(this.currentPage * this.pageSize, filteredCount);
        message = `Showing ${start}-${end} of ${filteredCount} results`;
      } else if (filteredCount === this.productEntries.length) {
        message = `Showing all ${filteredCount} results`;
      }

      this.querySelectorAll('[data-filter-count]').forEach((count) => {
        count.textContent = message;
      });
    }

    getSortedEntries(entries) {
      const sortedEntries = [...entries];
      if (this.sortValue === 'price-ascending') {
        sortedEntries.sort((first, second) => first.price - second.price || first.index - second.index);
      } else if (this.sortValue === 'price-descending') {
        sortedEntries.sort((first, second) => second.price - first.price || first.index - second.index);
      } else if (this.sortValue === 'title-ascending') {
        sortedEntries.sort((first, second) => first.title.localeCompare(second.title));
      } else if (this.sortValue === 'title-descending') {
        sortedEntries.sort((first, second) => second.title.localeCompare(first.title));
      } else {
        sortedEntries.sort((first, second) => first.index - second.index);
      }

      return sortedEntries;
    }

    renderPage(entries) {
      if (!this.grid) return;

      const sortedEntries = this.getSortedEntries(entries);
      const pageCount = Math.max(1, Math.ceil(entries.length / this.pageSize));
      this.currentPage = Math.max(1, Math.min(this.currentPage, pageCount));
      const pageStart = (this.currentPage - 1) * this.pageSize;
      const pageEntries = new Set(sortedEntries.slice(pageStart, pageStart + this.pageSize));

      this.productEntries.forEach((entry) => {
        entry.element.hidden = !pageEntries.has(entry);
      });

      sortedEntries.forEach((entry) => this.grid.append(entry.element));
      this.updateResultCount(entries.length);
    }

    renderPagination(totalCount) {
      const pageCount = Math.ceil(totalCount / this.pageSize);
      const paginationItems = this.getPaginationItems(pageCount, this.currentPage);

      this.querySelectorAll('[data-filter-pagination]').forEach((pagination) => {
        pagination.replaceChildren();
        pagination.hidden = pageCount <= 1;
        if (pageCount <= 1) return;

        pagination.append(this.createPageButton(this.currentPage - 1, 'Previous page', this.currentPage === 1, 'previous'));

        paginationItems.forEach((item) => {
          if (item === 'ellipsis') {
            const ellipsis = document.createElement('span');
            ellipsis.className = 'featured-collection-filter__pagination-ellipsis';
            ellipsis.textContent = '...';
            ellipsis.setAttribute('aria-hidden', 'true');
            pagination.append(ellipsis);
            return;
          }

          pagination.append(this.createPageButton(item, `Page ${item}`, false));
        });

        pagination.append(this.createPageButton(this.currentPage + 1, 'Next page', this.currentPage === pageCount, 'next'));
      });
    }

    getPaginationItems(pageCount, currentPage) {
      if (pageCount <= 5) return Array.from({ length: pageCount }, (_value, index) => index + 1);

      const candidates = new Set([1, 2, 3, pageCount, currentPage - 1, currentPage, currentPage + 1]);
      const pages = [...candidates]
        .filter((page) => page >= 1 && page <= pageCount)
        .sort((first, second) => first - second);
      const items = [];

      pages.forEach((page, index) => {
        if (index > 0 && page - pages[index - 1] > 1) items.push('ellipsis');
        items.push(page);
      });

      return items;
    }

    createPageButton(page, label, disabled, direction) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'featured-collection-filter__pagination-button';
      if (direction) button.classList.add(`featured-collection-filter__pagination-button--${direction}`);
      button.dataset.filterPage = Math.max(1, page);
      button.disabled = disabled;
      button.setAttribute('aria-label', label);

      if (direction) {
        const icon = document.createElement('span');
        icon.className = 'featured-collection-filter__pagination-arrow';
        icon.setAttribute('aria-hidden', 'true');
        const iconUrl = direction === 'previous' ? this.previousIconUrl : this.nextIconUrl;
        if (iconUrl) {
          const image = document.createElement('img');
          image.src = iconUrl;
          image.alt = '';
          image.setAttribute('aria-hidden', 'true');
          icon.append(image);
        }
        button.append(icon);
      } else {
        button.textContent = page;
        if (page === this.currentPage) {
          button.classList.add('is-current');
          button.setAttribute('aria-current', 'page');
        }
      }

      return button;
    }

    focusPageButton(page) {
      const button = this.querySelector(`[data-filter-page="${page}"].is-current`);
      button?.focus();
    }

    removeFilter(kind, value) {
      if (kind === 'category') {
        this.selectedCategories.delete(value);
        this.syncCheckboxes(kind, value, false);
      } else if (kind === 'availability') {
        this.selectedAvailability.delete(value);
        this.syncCheckboxes(kind, value, false);
      } else if (kind === 'color') {
        this.selectedColors.delete(value);
        this.syncCheckboxes(kind, value, false);
      } else if (kind === 'price') {
        this.priceMin = 0;
        this.priceMax = this.priceLimit;
        this.syncPriceControls();
      }

      this.applyFilters();
    }

    clearFilters() {
      this.selectedCategories.clear();
      this.selectedAvailability.clear();
      this.selectedColors.clear();
      this.priceMin = 0;
      this.priceMax = this.priceLimit;
      this.querySelectorAll('[data-filter-kind]').forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.syncPriceControls();
      this.applyFilters();
    }

    openDrawer(openButton) {
      this.lastFocusedElement = openButton;
      this.classList.add('is-drawer-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawerStackingContext?.classList.add('featured-filter-drawer-layer');
      document.body.classList.add('featured-filter-drawer-open');
      window.requestAnimationFrame(() => {
        this.querySelector('[data-filter-close]')?.focus();
      });
    }

    closeDrawer() {
      if (!this.classList.contains('is-drawer-open')) return;
      this.classList.remove('is-drawer-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawerStackingContext?.classList.remove('featured-filter-drawer-layer');
      document.body.classList.remove('featured-filter-drawer-open');
      this.lastFocusedElement?.focus();
    }

    handleKeydown(event) {
      if (!this.classList.contains('is-drawer-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusableElements = [
        ...this.drawer.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((element) => element.offsetParent !== null);
      if (!focusableElements.length) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    handleResize() {
      if (window.innerWidth >= 990) this.closeDrawer();
    }
  }

  customElements.define('featured-collection-filter', FeaturedCollectionFilter);
}
